import React, { createContext, useContext, useMemo } from "react"
import { graphql, useStaticQuery } from "gatsby"
import { DEFAULT_LOCALE, FALLBACK, localizedPath } from "../i18n/config"

// The language comes from the URL: gatsby-node puts { lang, basePath, locales } in every
// page's context, and wrapPageElement (gatsby-browser.js / gatsby-ssr.js) feeds it in here.
const LanguageContext = createContext({
  lang: DEFAULT_LOCALE,
  basePath: "/",
  locales: [DEFAULT_LOCALE],
})

const LanguageProvider = ({ pageContext = {}, children }) => {
  const value = useMemo(
    () => ({
      lang: pageContext.lang || DEFAULT_LOCALE,
      basePath: pageContext.basePath || "/",
      locales: pageContext.locales || [DEFAULT_LOCALE],
    }),
    [pageContext.lang, pageContext.basePath, pageContext.locales]
  )
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

const useLanguage = () => useContext(LanguageContext)

// Picks the entry for `lang` from a { ja, en, pt } object, falling back pt -> en -> ja.
const pick = (byLang, lang) => {
  for (let l = lang; l; l = FALLBACK[l]) {
    if (byLang?.[l] != null) return byLang[l]
  }
  return byLang?.[DEFAULT_LOCALE]
}

// Returns a function that localises an internal path for the current language, falling back
// to another language when the page isn't built in this one (Portuguese only covers a few).
const useLocalePath = () => {
  const { lang } = useLanguage()
  const { allSitePage } = useStaticQuery(graphql`
    query {
      allSitePage {
        nodes {
          path
        }
      }
    }
  `)
  const paths = useMemo(
    () => new Set(allSitePage.nodes.map(n => n.path)),
    [allSitePage]
  )

  return (to, targetLang = lang) => {
    const [basePath, hash] = to.split("#")
    const suffix = hash ? `#${hash}` : ""
    for (let l = targetLang; l; l = FALLBACK[l]) {
      const candidate = localizedPath(basePath, l)
      if (paths.has(candidate)) return candidate + suffix
    }
    return to
  }
}

export { LanguageContext, LanguageProvider, useLanguage, useLocalePath, pick }
